import { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import {
  Droplets, Building2, Calendar, ArrowRight, Search, Filter,
  ChevronRight, ChevronDown, Beaker, Edit3, Trash2, ArrowLeft, Plus,
  FlaskConical, TrendingUp, AlertTriangle, CheckCircle2,
  Clock, Hash, Globe, LayoutList, LayoutGrid, AlignJustify, ShieldCheck,
  PenLine, Printer, X, Download, MapPin,
} from 'lucide-react';
import { useNavigate } from 'react-router';
import { useAuth } from '@/hooks/useAuth';
import { hasPhpBackend, apiListInfluentSamples, apiListCustomerSites, apiListCustomers } from '@/lib/phpApi';
import { useSession } from '@/context/SessionContext';
import { getRetentionPolicy, isWithinRetention, retentionToastMessage, adminOverridePrompt } from '@/lib/retention';
import { getStandard, ALL_STANDARDS } from '@/types/standards';
import { generateTrendReportPDF, buildRegulatoryReportNumber } from '@/lib/pdfGenerator';
import { getAllLocalSamples, deleteLocalInfluentSample } from '@/lib/hybridStorage';
import AddSampleDialog from '@/components/AddSampleDialog';
import ConfirmDialog from '@/components/ConfirmDialog';
import RegulatoryNotice from '@/components/RegulatoryNotice';
import PageHeader from '@/components/PageHeader';

interface Sample {
  id: number;
  customerId: number | null;
  siteId?: number | null;
  userId?: number | null;
  sampleName: string;
  reportNumber: string;
  params: Record<string, number>;
  limits?: Record<string, number> | null;
  standard: string;
  sampleType: 'influent' | 'effluent';
  linkedSampleId?: number | null;
  notes?: string | null;
  createdAt: string;
}

interface Customer {
  id: number;
  name: string;
  company?: string | null;
}

/* ─── END types ─── */

const PARAM_LABELS: Record<string, string> = {
  // Legacy keys
  flowRate: 'Flow', bod: 'BOD\u2085', nh3: 'NH\u2083', oandg: 'O&G', og: 'O&G',
  // New keys (from InfluentEntryPage)
  bod5: 'BOD\u2085', nh3n: 'NH\u2083-N', oilgrease: 'O&G',
  tp: 'Total P', tn: 'Total N',
  no3n: 'NO\u2083-N', cadmium: 'Cd', nickel: 'Ni', zinc: 'Zn',
  copper: 'Cu', iron: 'Fe', manganese: 'Mn', arsenic: 'As',
  turbidity: 'Turbidity', flow: 'Flow', sulfide: 'Sulfides',
  chloride: 'Cl\u207B', hardness: 'Hardness', alkalinity: 'Alkalinity',
  mlss: 'MLSS', srt: 'SRT', do: 'DO',
  // Common
  cod: 'COD', tss: 'TSS', ph: 'pH',
  lead: 'Pb', chromium: 'Cr',
  tds: 'TDS', conductivity: 'Cond.', temperature: 'Temp',
};
const PARAM_UNITS: Record<string, string> = {
  flowRate: 'm\u00B3/d', flow: 'm\u00B3/d',
  bod: 'mg/L', bod5: 'mg/L', cod: 'mg/L', tss: 'mg/L', ph: '',
  nh3: 'mg/L', nh3n: 'mg/L', oandg: 'mg/L', og: 'mg/L', oilgrease: 'mg/L',
  tp: 'mg/L', tn: 'mg/L', no3n: 'mg/L',
  lead: 'mg/L', chromium: 'mg/L', cadmium: 'mg/L', nickel: 'mg/L',
  zinc: 'mg/L', copper: 'mg/L', iron: 'mg/L', manganese: 'mg/L', arsenic: 'mg/L',
  tds: 'mg/L', conductivity: '\u00B5S/cm', temperature: '\u00B0C',
  turbidity: 'NTU', sulfide: 'mg/L', chloride: 'mg/L',
  hardness: 'mg/L', alkalinity: 'mg/L', mlss: 'mg/L', srt: 'd', do: 'mg/L',
};
// Priority params shown in compact / card views \u2014 new keys take priority
const CORE_PARAMS = [
  'ph', 'cod', 'bod5', 'bod', 'tss', 'nh3n', 'nh3', 'oilgrease', 'oandg', 'og',
  'tp', 'tn', 'tds', 'lead', 'chromium', 'cadmium', 'conductivity', 'temperature', 'flowRate', 'flow',
];

/* ─── Helpers ─── */
function loadAllInfluentSamples(): Sample[] {
  // Reads from BOTH rb_influent_samples_v2 (new) and rb_customer_samples (legacy)
  return getAllLocalSamples()
    .filter(s => !s.sampleType || s.sampleType === 'influent')
    .map(s => ({
      id: s.id,
      customerId: s.customerId,
      siteId: (s as any).siteId != null ? Number((s as any).siteId) : null,
      userId: (s as any).userId != null ? Number((s as any).userId) : null,
      sampleName: s.sampleName,
      reportNumber: s.reportNumber || buildRegulatoryReportNumber('INF', s.standard || 'UK_EA'),
      params: s.params,
      limits: null,
      standard: s.standard || 'UK_EA',
      sampleType: 'influent' as const,
      linkedSampleId: s.linkedSampleId,
      notes: s.notes,
      createdAt: s.createdAt,
    }));
}
function deleteSampleFromAllStores(id: number) {
  // Remove from new store
  deleteLocalInfluentSample(id);
  // Also remove from legacy store
  try {
    const legacy = JSON.parse(localStorage.getItem('rb_customer_samples') || '[]');
    localStorage.setItem('rb_customer_samples', JSON.stringify(legacy.filter((s: any) => s.id !== id)));
  } catch { /* ignore */ }
}
/** For the quick-add dialog which still saves to legacy store */
function saveLegacySample(sample: Sample) {
  try {
    const existing = JSON.parse(localStorage.getItem('rb_customer_samples') || '[]');
    localStorage.setItem('rb_customer_samples', JSON.stringify([sample, ...existing]));
  } catch { /* ignore */ }
}

function SamplePrintModal({ sample, customers, sites, onClose }: {
  sample: Sample;
  customers: { id: number; name: string; company?: string | null }[];
  sites: { id: number; name: string }[];
  onClose: () => void;
}) {
  const stdInfo = getStandard(sample.standard as any);
  const customer = customers.find(c => c.id === sample.customerId);
  const site = sites.find(s => s.id === sample.siteId);
  const params = sample.params || {};

  const PARAM_PRINT_LABELS: Record<string, { label: string; unit: string }> = {
    ph: { label: 'pH', unit: '' },
    cod: { label: 'COD', unit: 'mg/L' },
    bod5: { label: 'BOD₅', unit: 'mg/L' },
    bod: { label: 'BOD', unit: 'mg/L' },
    tss: { label: 'TSS', unit: 'mg/L' },
    nh3n: { label: 'NH₃-N', unit: 'mg/L' },
    tp: { label: 'Total P', unit: 'mg/L' },
    tn: { label: 'Total N', unit: 'mg/L' },
    oilgrease: { label: 'Oil & Grease', unit: 'mg/L' },
    oilGrease: { label: 'Oil & Grease', unit: 'mg/L' },
    tds: { label: 'TDS', unit: 'mg/L' },
    conductivity: { label: 'Conductivity', unit: 'µS/cm' },
    temperature: { label: 'Temperature', unit: '°C' },
    turbidity: { label: 'Turbidity', unit: 'NTU' },
    lead: { label: 'Lead (Pb)', unit: 'mg/L' },
    chromium: { label: 'Chromium (Cr)', unit: 'mg/L' },
    cadmium: { label: 'Cadmium (Cd)', unit: 'mg/L' },
    nickel: { label: 'Nickel (Ni)', unit: 'mg/L' },
    zinc: { label: 'Zinc (Zn)', unit: 'mg/L' },
    copper: { label: 'Copper (Cu)', unit: 'mg/L' },
    iron: { label: 'Iron (Fe)', unit: 'mg/L' },
    manganese: { label: 'Manganese (Mn)', unit: 'mg/L' },
    arsenic: { label: 'Arsenic (As)', unit: 'mg/L' },
    mercury: { label: 'Mercury (Hg)', unit: 'mg/L' },
    no3n: { label: 'NO₃-N', unit: 'mg/L' },
    sulfide: { label: 'Sulfides', unit: 'mg/L' },
    chloride: { label: 'Chlorides', unit: 'mg/L' },
    hardness: { label: 'Hardness', unit: 'mg/L CaCO₃' },
    alkalinity: { label: 'Alkalinity', unit: 'mg/L CaCO₃' },
    totalNitrogen: { label: 'Total N', unit: 'mg/L' },
    totalPhosphorus: { label: 'Total P', unit: 'mg/L' },
    ammoniaN: { label: 'NH₃-N', unit: 'mg/L' },
    tkn: { label: 'TKN', unit: 'mg/L' },
    flowRate: { label: 'Flow Rate', unit: 'm³/d' },
    flow: { label: 'Flow', unit: 'm³/d' },
  };

  const REQ_PARAMS: Record<string, string[]> = {
    UK_EA: ['ph', 'cod', 'bod5', 'tss', 'nh3n', 'oilgrease', 'tds', 'conductivity', 'temperature'],
    AU_EPA: ['ph', 'tss', 'bod5', 'cod', 'tn', 'tp', 'nh3n', 'oilgrease', 'conductivity', 'lead', 'chromium', 'cadmium', 'mercury', 'temperature'],
    EU_UWWTD: ['ph', 'cod', 'bod5', 'tss', 'tp', 'tn', 'nh3n'],
  };
  const reqParams = REQ_PARAMS[sample.standard] || REQ_PARAMS['UK_EA'];

  const REG_REFS: Record<string, { body: string; legislation: string }> = {
    UK_EA: { body: 'Environment Agency (EA)', legislation: 'Environmental Permitting (England & Wales) Regulations 2016 / Water Industry Act 1991 s118 / EPR 2016' },
    AU_EPA: { body: 'Australian EPA', legislation: 'ANZECC/ARMCANZ 2000 Water Quality Guidelines — Schedule B1 (13 Ambient Monitoring Parameters)' },
    EU_UWWTD: { body: 'EU — UWWTD', legislation: 'Urban Wastewater Treatment Directive 91/271/EEC Article 4/5' },
  };
  const regRef = REG_REFS[sample.standard] || REG_REFS['UK_EA'];

  const handlePrint = () => {
    const originalTitle = document.title;
    document.title = `${sample.reportNumber} — Influent Sample Report`;
    window.print();
    document.title = originalTitle;
  };

  const handleDownloadPDF = () => {
    generateTrendReportPDF({
      id: String(sample.id),
      reportNumber: sample.reportNumber || buildRegulatoryReportNumber('INF', (sample.standard || 'UK_EA') as any),
      sampleName: sample.sampleName,
      customerName: customer ? `${customer.name}${customer.company ? ` — ${customer.company}` : ''}` : 'Unassigned',
      customerCompany: customer?.company || '',
      standard: sample.standard || 'UK_EA',
      date: new Date(sample.createdAt),
      preparerName: 'Reynolds & Bauhm',
      preparerCompany: 'Reynolds & Bauhm Ltd',
      params: sample.params || {},
    });
  };

  const paramEntries = Object.entries(params).filter(([, v]) => typeof v === 'number' && !isNaN(v as number));
  const reqMet = reqParams.filter(p => params[p] !== undefined).length;
  const reqTotal = reqParams.length;

  return (
    <>
      <style>{`
        @media print {
          @page { size: A4 portrait; margin: 12mm; }
          body * { visibility: hidden; }
          #sample-print-content, #sample-print-content * { visibility: visible; }
          #sample-print-content {
            position: absolute; left: 0; top: 0; width: 100%;
            background: white !important; color: black !important;
            font-size: 9pt !important;
          }
          .no-print { display: none !important; }
        }
      `}</style>
      <div className="fixed inset-0 z-[100] flex items-start justify-center overflow-y-auto bg-black/60 p-4">
        <div className="relative w-full max-w-[860px] bg-rb-surface rounded-xl shadow-2xl my-8">
          {/* Modal header */}
          <div className="no-print flex items-center justify-between p-4 border-b border-slate-800/60 bg-rb-elevated rounded-t-xl">
            <div className="flex items-center gap-2">
              <Printer className="w-5 h-5 text-slate-600" />
              <h2 className="text-sm font-bold text-slate-400">Influent Sample Report — Print Preview</h2>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={handleDownloadPDF} className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-bold bg-rb-brand text-slate-200 hover:bg-[#b8931f] transition-colors">
                <Download className="w-4 h-4" /> Download PDF
              </button>
              <button onClick={handlePrint} className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-bold bg-slate-900 text-white hover:bg-slate-800 transition-colors">
                <Printer className="w-4 h-4" /> Print
              </button>
              <button onClick={onClose} className="p-2 rounded-lg text-slate-400 hover:text-slate-400 hover:bg-rb-elevated transition-colors">
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Report content */}
          <div id="sample-print-content" className="p-8 bg-rb-surface text-slate-200">
            {/* Header border box */}
            <div className="border-2 border-slate-800 mb-5">
              <div className="flex items-stretch border-b border-slate-800">
                <div className="flex-1 p-4">
                  <p className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-500">Reynolds &amp; Bauhm Ltd</p>
                  <p className="text-[10px] text-slate-400 mb-2">Wastewater Treatment Engineering · Environmental Solutions</p>
                  <h1 className="text-lg font-black text-slate-200">Influent Water Quality Analysis</h1>
                  <p className="text-sm font-semibold text-slate-600">{sample.sampleName}</p>
                  <div className="flex items-center gap-1.5 mt-1">
                    <span className="text-sm">{stdInfo.flag}</span>
                    <p className="text-[10px] text-slate-500 font-semibold">{regRef.body} — {sample.standard.replace('_', ' ')}</p>
                  </div>
                </div>
                <div className="w-48 shrink-0 border-l border-slate-800 bg-slate-900 text-white flex flex-col divide-y divide-slate-700">
                  <div className="px-3 py-2">
                    <p className="text-[10px] uppercase tracking-widest text-slate-400 mb-0.5">Report Number</p>
                    <p className="text-[12px] font-black font-mono text-rb-brand">{sample.reportNumber}</p>
                  </div>
                  <div className="px-3 py-2">
                    <p className="text-[10px] uppercase tracking-widest text-slate-400 mb-0.5">Date</p>
                    <p className="text-[11px] font-semibold text-white">{new Date(sample.createdAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}</p>
                  </div>
                  <div className="px-3 py-2">
                    <p className="text-[10px] uppercase tracking-widest text-slate-400 mb-0.5">Compliance</p>
                    <p className="text-[11px] font-bold text-emerald-400">{reqMet}/{reqTotal} req. params</p>
                  </div>
                </div>
              </div>
              {/* Metadata strip */}
              <div className="grid grid-cols-3 divide-x divide-slate-800 bg-rb-elevated">
                <div className="px-4 py-2">
                  <p className="text-[10px] uppercase tracking-widest text-slate-400 mb-0.5">Client / Company</p>
                  <p className="text-[11px] font-semibold text-slate-300">{customer ? `${customer.name}${customer.company ? ` — ${customer.company}` : ''}` : 'Unassigned'}</p>
                </div>
                <div className="px-4 py-2">
                  <p className="text-[10px] uppercase tracking-widest text-slate-400 mb-0.5">Site / Location</p>
                  <p className="text-[11px] font-semibold text-slate-300">{site ? site.name : '—'}</p>
                </div>
                <div className="px-4 py-2">
                  <p className="text-[10px] uppercase tracking-widest text-slate-400 mb-0.5">Regulatory Ref.</p>
                  <p className="text-[10px] text-slate-600">{regRef.legislation}</p>
                </div>
              </div>
            </div>

            {/* Parameters table */}
            <h3 className="text-xs font-bold text-slate-600 uppercase mb-2 border-b border-slate-800/60 pb-1">Measured Parameters</h3>
            <table className="w-full text-xs border-collapse mb-5">
              <thead>
                <tr className="bg-rb-elevated">
                  <th className="text-left p-1.5 font-bold text-slate-600 border border-slate-800/60 w-[40%]">Parameter</th>
                  <th className="text-right p-1.5 font-bold text-slate-600 border border-slate-800/60 w-[30%]">Measured Value</th>
                  <th className="text-center p-1.5 font-bold text-slate-600 border border-slate-800/60 w-[15%]">Unit</th>
                  <th className="text-center p-1.5 font-bold text-slate-600 border border-slate-800/60 w-[15%]">Required</th>
                </tr>
              </thead>
              <tbody>
                {paramEntries.map(([key, val]) => {
                  const meta = PARAM_PRINT_LABELS[key];
                  const isReq = reqParams.includes(key);
                  return (
                    <tr key={key} className={isReq ? 'bg-indigo-500/10' : ''}>
                      <td className="p-1.5 border border-slate-800/60 font-medium text-slate-300">{meta ? meta.label : key.toUpperCase()}</td>
                      <td className="p-1.5 border border-slate-800/60 text-right font-bold font-mono text-slate-200">{typeof val === 'number' ? val.toFixed(key === 'ph' ? 1 : key.includes('lead') || key.includes('Cr') || key.includes('Cd') ? 3 : 1) : val}</td>
                      <td className="p-1.5 border border-slate-800/60 text-center text-slate-500">{meta ? meta.unit : '—'}</td>
                      <td className="p-1.5 border border-slate-800/60 text-center">{isReq ? <span className="text-[10px] font-bold text-indigo-400">✓ REQ</span> : <span className="text-[10px] text-slate-400">—</span>}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            {/* Notes */}
            {sample.notes && (
              <div className="mb-5 p-3 rb-inner-card-sm">
                <p className="text-[10px] font-bold text-slate-500 uppercase mb-1">Sample Notes</p>
                <p className="text-xs text-slate-400">{sample.notes}</p>
              </div>
            )}

            {/* Footer */}
            <div className="border-t-2 border-slate-800 pt-2 flex items-center justify-between mt-4">
              <p className="text-[10px] text-slate-500">Reynolds &amp; Bauhm Ltd · Smart Wastewater Tool · Influent Sample Record</p>
              <p className="text-[10px] font-mono font-bold text-rb-brand">{sample.reportNumber}</p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default function InfluentPage() {
  const navigate = useNavigate();
  const { isAuthenticated, isAdmin, user } = useAuth();
  const { currentCustomer, activeStandard } = useSession();

  const [samples, setSamples] = useState<Sample[]>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [selectedCustomerId, setSelectedCustomerId] = useState<number | 'all'>('all');
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [editSample, setEditSample] = useState<Sample | null>(null);
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [hoveredId, setHoveredId] = useState<number | null>(null);
  const [expandedRowId, setExpandedRowId] = useState<number | null>(null);
  type ViewMode = 'compact' | 'cards' | 'detail';
  const [viewMode, setViewMode] = useState<ViewMode>('compact');
  const [sites, setSites] = useState<{ id: number; name: string; customerId: number | null }[]>([]);
  const [selectedSiteId, setSelectedSiteId] = useState<number | 'all'>('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [printSample, setPrintSample] = useState<Sample | null>(null);

  const loadData = () => {
    // CONFIDENTIALITY GATE: if no active session customer, show nothing
    if (!currentCustomer) {
      setSamples([]);
      setLoading(false);
      return;
    }

    setLoading(true);

    // Resolve the DB integer ID for the active session customer.
    // serverId takes priority (set when activating from server-loaded list).
    // Fall back to id only when it looks like a real DB integer (< 2 billion).
    const sessionCustId = (currentCustomer as any).serverId
      ?? (currentCustomer.id.length < 13 && Number(currentCustomer.id) > 0 ? Number(currentCustomer.id) : 0);

    // Local customers as immediate fallback (replaced below when server responds)
    const localCust: Customer[] = JSON.parse(localStorage.getItem('rb_customers') || '[]');
    setCustomers(localCust);

    // Filter local samples to ONLY the current user AND current session customer
    const currentUserId = user ? Number(user.id) : 0;
    const allLocal = loadAllInfluentSamples().filter(s =>
      (!currentUserId || !(s as any).userId || (s as any).userId === currentUserId) &&
      (s.customerId != null ? Number(s.customerId) === sessionCustId : false)
    );

    if (isAuthenticated && hasPhpBackend()) {
      // Admin sees all users; regular users see only own
      // Always scope to session customer via customerId parameter
      const opts = isAdmin ? { adminAll: true } : {};
      Promise.all([
        apiListInfluentSamples(sessionCustId, opts).catch(() => [] as any[]),
        apiListCustomerSites().catch(() => [] as any[]),
        apiListCustomers().catch(() => [] as any[]),
      ]).then(([rows, siteRows, custRows]) => {
        // Server customers supersede local ones
        const serverCusts: Customer[] = (custRows || []).map((c: any) => ({
          id: Number(c.id),
          name: c.name,
          company: c.company ?? null,
        }));
        // Keep any purely-local customers that have no server equivalent
        const mergedCusts: Customer[] = [
          ...serverCusts,
          ...localCust.filter(lc => !serverCusts.some(sc => String(sc.id) === String(lc.id))),
        ];
        setCustomers(mergedCusts);

        // Sites
        const serverSites = (siteRows || []).map((s: any) => ({
          id: Number(s.id),
          name: s.name,
          customerId: (s.customerId ?? s.customer_id) != null ? Number(s.customerId ?? s.customer_id) : null,
        }));
        setSites(serverSites);

        // Samples — server first, then non-overlapping local
        const server = (rows || []).filter((r: any) => (r.sampleType ?? r.sample_type ?? 'influent') === 'influent');
        const merged: Sample[] = [
          ...server.map((r: any) => ({
            id: Number(r.id),
            customerId: (r.customerId ?? r.customer_id) != null ? Number(r.customerId ?? r.customer_id) : null,
            siteId: (r.siteId ?? r.site_id) != null ? Number(r.siteId ?? r.site_id) : null,
            userId: (r.userId ?? r.user_id) != null ? Number(r.userId ?? r.user_id) : null,
            sampleName: r.sampleName || r.sample_name || 'Untitled',
            reportNumber: r.reportNumber || r.report_number || buildRegulatoryReportNumber('INF', r.standard || 'UK_EA'),
            params: typeof r.params === 'string' ? JSON.parse(r.params) : (r.params || {}),
            limits: typeof r.limits === 'string' ? JSON.parse(r.limits) : (r.limits || null),
            standard: r.standard || 'UK_EA',
            sampleType: 'influent' as const,
            linkedSampleId: r.linkedSampleId ?? r.linked_sample_id ?? null,
            notes: r.notes || null,
            createdAt: r.createdAt || r.created_at,
          })),
          ...allLocal.filter((ls: Sample) => !server.some((ss: any) => ss.id === ls.id)),
        ];
        setSamples(merged);
      }).catch(() => {
        setSamples(allLocal);
      }).finally(() => setLoading(false));
    } else {
      // Offline — use local data only
      try {
        const localSites = JSON.parse(localStorage.getItem('rb_customer_sites') || '[]');
        setSites(localSites.map((s: any) => ({ id: s.id, name: s.name, customerId: s.customerId ?? null })));
      } catch { /* ignore */ }
      setSamples(allLocal);
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); }, [isAuthenticated, currentCustomer?.id]);

  // Auto-filter to active customer when page loads or customer changes
  useEffect(() => {
    if (currentCustomer) {
      const numId = parseInt(currentCustomer.id);
      if (!isNaN(numId)) setSelectedCustomerId(numId);
    } else {
      setSelectedCustomerId('all');
      setSamples([]);
    }
  }, [currentCustomer?.id]);

  const filtered = useMemo(() => {
    let rows = samples;
    if (selectedCustomerId !== 'all') rows = rows.filter(s => s.customerId === selectedCustomerId);
    if (selectedSiteId !== 'all') rows = rows.filter(s => s.siteId === selectedSiteId);
    if (dateFrom) rows = rows.filter(s => new Date(s.createdAt) >= new Date(dateFrom));
    if (dateTo) {
      const to = new Date(dateTo);
      to.setHours(23, 59, 59, 999);
      rows = rows.filter(s => new Date(s.createdAt) <= to);
    }
    if (search.trim()) {
      const q = search.toLowerCase();
      rows = rows.filter(s =>
        s.sampleName.toLowerCase().includes(q) ||
        s.reportNumber.toLowerCase().includes(q) ||
        (s.notes || '').toLowerCase().includes(q)
      );
    }
    return rows.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
  }, [samples, selectedCustomerId, selectedSiteId, dateFrom, dateTo, search]);

  const handleDelete = () => {
    if (!deleteId) return;
    deleteSampleFromAllStores(deleteId);
    setSamples(prev => prev.filter(s => s.id !== deleteId));
    toast.info('Sample deleted');
    setDeleteId(null);
  };

  /** Retention-aware delete entry point — called by trash buttons */
  const requestDeleteSample = (s: Sample) => {
    const policy = getRetentionPolicy(s.standard);
    if (isWithinRetention(s.createdAt, policy)) {
      if (!isAdmin) {
        toast.error(retentionToastMessage(policy, s.createdAt));
        return;
      }
      // Admin override — warn and require explicit confirmation
      if (!window.confirm(adminOverridePrompt(policy, s.createdAt, 'influent sample'))) return;
      // Proceed directly — skip the generic ConfirmDialog
      deleteSampleFromAllStores(s.id);
      setSamples(prev => prev.filter(x => x.id !== s.id));
      toast.warning('Sample deleted (admin override — within retention period)');
      return;
    }
    // Outside retention window — normal ConfirmDialog flow
    setDeleteId(s.id);
  };

  const handleAddSave = (data: { customerId: number; sampleName: string; params: Record<string, number>; standard: string; notes: string; assess: boolean }) => {
    const newId = Date.now();
    const newSample: Sample = {
      id: newId,
      customerId: data.customerId,
      sampleName: data.sampleName,
      reportNumber: buildRegulatoryReportNumber('INF', data.standard || 'UK_EA'),
      params: data.params,
      limits: null,
      standard: data.standard,
      sampleType: 'influent',
      linkedSampleId: null,
      notes: data.notes || null,
      createdAt: new Date().toISOString(),
    };
    saveLegacySample(newSample);
    setSamples(prev => [newSample, ...prev.filter(s => s.id !== newId)]);
    toast.success(`Influent sample "${data.sampleName}" saved`);
    if (data.assess) {
      toast.success('Assessment recommendations saved — visit Equipment List');
    }
    setShowAddDialog(false);
  };

  const getCustomerName = (cid: number | null) => {
    if (!cid) return 'Unassigned';
    const c = customers.find(x => x.id === cid);
    return c ? c.name + (c.company ? ` (${c.company})` : '') : `Customer #${cid}`;
  };

  const getSiteName = (siteId: number | null | undefined) => {
    if (!siteId) return null;
    const s = sites.find(x => x.id === siteId);
    return s ? s.name : null;
  };

  const getParamColor = (key: string, val: number): string => {
    // Use active standard limits for color thresholds where available
    const activeStd = ALL_STANDARDS.find(s => s.code === (activeStandard || 'UK_EA'));
    const L = activeStd?.limits;
    if (key === 'ph') {
      const lo = L?.phMin ?? 6; const hi = L?.phMax ?? 9;
      return (val < lo || val > hi) ? '#ef4444' : (val < lo + 0.5 || val > hi - 0.5) ? '#f59e0b' : '#10b981';
    }
    if (key === 'cod') { const lim = L?.cod ?? 500; return val > lim * 2 ? '#ef4444' : val > lim ? '#f59e0b' : '#10b981'; }
    if (key === 'tss') { const lim = L?.tss ?? 100; return val > lim * 3 ? '#ef4444' : val > lim ? '#f59e0b' : '#10b981'; }
    if (key === 'bod' || key === 'bod5') { const lim = L?.bod5 ?? 50; return val > lim * 5 ? '#ef4444' : val > lim ? '#f59e0b' : '#10b981'; }
    if (key === 'nh3n' || key === 'nh3') { const lim = L?.ammoniaN ?? 25; return val > lim * 3 ? '#ef4444' : val > lim ? '#f59e0b' : '#10b981'; }
    if (key === 'oilgrease' || key === 'oandg' || key === 'og') { const lim = L?.oilGrease ?? 30; return val > lim * 4 ? '#ef4444' : val > lim ? '#f59e0b' : '#10b981'; }
    return '#06b6d4';
  };

  const sampleCount = filtered.length;
  const canGenerateReport = sampleCount >= 3;

  // CONFIDENTIALITY GATE — if no active session customer, block the entire page
  if (!currentCustomer) {
    return (
      <div className="space-y-5">
        <PageHeader
          icon={Droplets}
          iconColor="text-cyan-400"
          title="Influent Water Samples"
          subtitle="Raw water entering the treatment process"
        />
        <div className="flex flex-col items-center justify-center py-20 rounded-2xl border border-amber-500/20 bg-amber-500/5">
          <Building2 className="w-12 h-12 text-amber-400/40 mb-4" />
          <p className="text-base font-bold text-amber-400 mb-1">No active customer session</p>
          <p className="text-sm text-slate-500 text-center max-w-sm">
            Select a customer from the Customers page to view influent sample records. Records are strictly scoped to the active session customer.
          </p>
          <button
            onClick={() => navigate('/customers')}
            className="mt-6 flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-bold bg-amber-500/10 text-amber-400 border border-amber-500/20 hover:bg-amber-500/20 transition-all"
          >
            <Building2 className="w-4 h-4" /> Go to Customers
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <PageHeader
        icon={Droplets}
        iconColor="text-cyan-400"
        title="Influent Water Samples"
        subtitle="Raw water entering the treatment process — all influent entries by date"
        actions={
          <div className="flex items-center gap-2 flex-wrap">
            <button onClick={() => navigate('/samples')} className="flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-xs font-bold bg-slate-800/80 border border-slate-800/60 text-slate-300 hover:bg-slate-700 hover:border-slate-600 transition-all">
              <ArrowLeft className="w-3.5 h-3.5" /> Samples
            </button>
            <button onClick={() => setShowAddDialog(true)} className="flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-xs font-bold bg-slate-800/80 border border-slate-800/60 text-slate-300 hover:bg-slate-700 hover:border-slate-600 transition-all">
              <Plus className="w-3.5 h-3.5" /> Quick Add
            </button>
            <button onClick={() => navigate('/influent-entry')} className="flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-xs font-bold bg-cyan-500/10 text-cyan-400 border border-cyan-500/20 hover:bg-cyan-500/20 transition-all">
              <PenLine className="w-3.5 h-3.5" /> Add Influent Sample
            </button>
          </div>
        }
      />

      {/* ─── REPORT STATUS BANNER ─── */}
      <div className={`p-3 rounded-xl border flex items-center gap-3 ${canGenerateReport ? 'border-emerald-500/20 bg-emerald-500/5' : 'border-amber-500/20 bg-amber-500/5'}`}>
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${canGenerateReport ? 'bg-emerald-500/15' : 'bg-amber-500/15'}`}>
          {canGenerateReport ? <CheckCircle2 className="w-4 h-4 text-emerald-400" /> : <AlertTriangle className="w-4 h-4 text-amber-400" />}
        </div>
        <div className="flex-1">
          <span className={`text-xs font-semibold ${canGenerateReport ? 'text-emerald-400' : 'text-amber-400'}`}>
            {canGenerateReport ? 'Report Ready — Trend analysis available' : 'More Samples Needed — Add influent samples to enable reporting'}
          </span>
          <span className="text-[10px] text-slate-500 ml-2">{sampleCount} sample{sampleCount !== 1 ? 's' : ''} shown ({Math.max(0, 3 - sampleCount)} more for report)</span>
        </div>
        {canGenerateReport && (
          <button onClick={() => navigate('/reports')} className="px-3 py-1.5 rounded-lg text-[10px] font-bold bg-emerald-500/15 text-emerald-400 border border-emerald-500/20 hover:bg-emerald-500/25 transition-all">
            Go to Reports →
          </button>
        )}
      </div>

      {/* ─── ACTIVE CUSTOMER BANNER ─── */}
      {currentCustomer && (
        <div className="flex items-center gap-3 px-4 py-2.5 rounded-xl border border-cyan-500/20 bg-cyan-500/5">
          <span className="w-2 h-2 rounded-full bg-cyan-400 animate-pulse shrink-0" />
          <Building2 className="w-3.5 h-3.5 text-cyan-400 shrink-0" />
          <span className="text-[10px] font-bold text-cyan-400 uppercase tracking-wider">Filtered for:</span>
          <span className="text-sm font-bold text-white">{currentCustomer.company}</span>
          {(currentCustomer as any).standard && (
            <span className="text-[10px] px-1.5 py-0.5 rounded border border-cyan-500/20 bg-cyan-500/10 text-cyan-400 font-bold">
              {(currentCustomer as any).standard.replace('_', ' ')}
            </span>
          )}
          <button onClick={() => setSelectedCustomerId('all')} title="Show all customers"
            className="ml-auto text-[10px] text-slate-500 hover:text-slate-300 px-2 py-1 rounded-lg hover:bg-slate-800/50 transition-all">
            Show All
          </button>
        </div>
      )}

      {/* ─── FILTERS + VIEW MODE ─── */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by name, report number, or notes..."
            className="w-full pl-9 pr-4 py-2.5 text-white text-sm focus:outline-none focus:ring-2 focus:ring-cyan-400/30 focus:border-cyan-400 rb-card" />
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Filter className="w-4 h-4 text-slate-500" />
          <select value={selectedCustomerId} onChange={(e) => setSelectedCustomerId(e.target.value === 'all' ? 'all' : parseInt(e.target.value))}
            className="px-3 py-2.5 text-white text-sm focus:outline-none focus:ring-2 focus:ring-cyan-400/30 rb-card">
            <option value="all">All Customers</option>
            {customers.map(c => (
              <option key={c.id} value={c.id}>{c.name}{c.company ? ` (${c.company})` : ''}</option>
            ))}
          </select>
          {/* Site filter */}
          {sites.length > 0 && (
            <select
              value={selectedSiteId}
              onChange={(e) => setSelectedSiteId(e.target.value === 'all' ? 'all' : parseInt(e.target.value))}
              className="px-3 py-2.5 text-white text-sm focus:outline-none focus:ring-2 focus:ring-cyan-400/30 rb-card"
            >
              <option value="all">All Sites</option>
              {sites.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          )}
          {/* Date range */}
          <input
            type="date"
            value={dateFrom}
            onChange={e => setDateFrom(e.target.value)}
            className="px-3 py-2.5 text-white text-sm focus:outline-none focus:ring-2 focus:ring-cyan-400/30 rb-card"
            title="From date"
          />
          <input
            type="date"
            value={dateTo}
            onChange={e => setDateTo(e.target.value)}
            className="px-3 py-2.5 text-white text-sm focus:outline-none focus:ring-2 focus:ring-cyan-400/30 rb-card"
            title="To date"
          />
        </div>

        {/* View mode toggle */}
        <div className="flex items-center gap-1 ml-auto p-1 rounded-xl bg-slate-800/60 border border-slate-800/50">
          {([
            { mode: 'compact' as const, icon: LayoutList, label: 'Compact List' },
            { mode: 'cards' as const, icon: LayoutGrid, label: 'Card Grid' },
            { mode: 'detail' as const, icon: AlignJustify, label: 'Full Detail' },
          ]).map(({ mode, icon: Icon, label }) => (
            <button
              key={mode}
              onClick={() => setViewMode(mode)}
              title={label}
              className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-[10px] font-bold transition-all ${
                viewMode === mode
                  ? 'bg-cyan-500/20 text-cyan-400 border border-cyan-500/30'
                  : 'text-slate-500 hover:text-slate-300'
              }`}
            >
              <Icon className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">{label}</span>
            </button>
          ))}
        </div>
      </div>

      <RegulatoryNotice standard={activeStandard} context="influent" compact />

      {/* ─── SAMPLE VIEWS ─── */}
      {loading ? (
        <div className="space-y-3">
          {[1, 2, 3, 4].map(i => <div key={i} className="h-14 bg-rb-elevated rounded-xl animate-pulse" />)}
        </div>
      ) : filtered.length === 0 ? (
        <div className="p-12 text-center rb-inner-card">
          <Droplets className="w-10 h-10 text-slate-600 mx-auto mb-3" />
          <p className="text-sm text-slate-400">No influent samples found</p>
          <p className="text-xs text-slate-600 mt-1 mb-4">Add raw water samples to begin treatment assessment</p>
          <button onClick={() => navigate('/influent-entry')} className="flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-bold bg-gradient-to-r from-cyan-500 to-blue-500 text-white hover:from-cyan-400 hover:to-blue-400 shadow-lg shadow-cyan-500/20 transition-all mx-auto">
            <PenLine className="w-4 h-4" /> Add First Influent Sample
          </button>
        </div>
      ) : (
        <AnimatePresence mode="wait">

          {/* ══ VIEW 1: COMPACT LIST with expandable rows ══ */}
          {viewMode === 'compact' && (
            <motion.div key="compact" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="space-y-2">
              <AnimatePresence>
                {filtered.map((s, i) => {
                  const isExpanded = expandedRowId === s.id;
                  const stdInfo = getStandard(s.standard as any);
                  const compactParams = ['ph', 'tss', 'bod5', 'bod', 'cod', 'nh3n', 'nh3'].filter(k => (s.params || {})[k] !== undefined).slice(0, 5);
                  const siteName = getSiteName(s.siteId);

                  // Compliance flags for expanded section
                  const REQ_PARAMS: Record<string, string[]> = {
                    UK_EA:    ['ph', 'cod', 'bod5', 'tss', 'nh3n', 'oilgrease', 'tds', 'conductivity', 'temperature'],
                    AU_EPA:   ['ph', 'tss', 'bod5', 'cod', 'tn', 'tp', 'nh3n', 'oilgrease', 'conductivity', 'lead', 'chromium', 'cadmium', 'mercury', 'temperature'],
                    EU_UWWTD: ['ph', 'cod', 'bod5', 'tss', 'tp', 'tn', 'nh3n'],
                    US_EPA:   ['ph', 'cod', 'bod5', 'tss', 'nh3n', 'oilgrease', 'conductivity', 'temperature'],
                  };
                  const reqParams = REQ_PARAMS[s.standard] || REQ_PARAMS['UK_EA'];
                  const params = s.params || {};
                  const allParamKeys = Object.keys(params);
                  const missingCount = reqParams.filter(k => params[k] === undefined).length;

                  return (
                    <motion.div
                      key={s.id}
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ delay: i * 0.025 }}
                      className={`rounded-xl border overflow-hidden transition-all ${
                        isExpanded
                          ? 'border-cyan-500/40 bg-rb-surface/70 shadow-lg shadow-cyan-500/5'
                          : 'border-slate-800 bg-rb-elevated hover:border-slate-700'
                      }`}
                    >
                      {/* ── Collapsed header (always visible) ── */}
                      <div
                        className="flex items-center gap-3 px-4 py-3 cursor-pointer select-none"
                        onClick={() => setExpandedRowId(isExpanded ? null : s.id)}
                        onMouseEnter={() => setHoveredId(s.id)}
                        onMouseLeave={() => setHoveredId(null)}
                      >
                        {/* Icon */}
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 transition-colors ${
                          isExpanded ? 'bg-cyan-500/15 border border-cyan-500/30' : 'bg-cyan-500/10 border border-cyan-500/20'
                        }`}>
                          <Droplets className="w-4 h-4 text-cyan-400" />
                        </div>

                        {/* Main info */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-sm font-semibold text-slate-200 truncate">{s.sampleName}</span>
                            <span className="text-[9px] font-mono text-rb-brand font-bold">{s.reportNumber}</span>
                            <span className="text-sm shrink-0">{stdInfo.flag}</span>
                          </div>
                          <div className="flex items-center gap-3 mt-0.5 flex-wrap">
                            <span className="text-[10px] text-slate-500 flex items-center gap-1">
                              <Building2 className="w-2.5 h-2.5" />{getCustomerName(s.customerId)}
                            </span>
                            {siteName && (
                              <span className="text-[10px] text-slate-500 flex items-center gap-1">
                                <MapPin className="w-2.5 h-2.5" />{siteName}
                              </span>
                            )}
                            <span className="text-[10px] text-slate-500 flex items-center gap-1">
                              <Calendar className="w-2.5 h-2.5" />
                              {new Date(s.createdAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                              {' '}
                              <span className="text-slate-600">{new Date(s.createdAt).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}</span>
                            </span>
                            {/* compact param chips */}
                            {compactParams.map(k => {
                              const val = params[k];
                              const color = getParamColor(k, val);
                              return (
                                <span key={k} className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-slate-800/60 border border-slate-800/40" style={{ color }}>
                                  {(PARAM_LABELS[k] || k).toUpperCase()} {val.toFixed(1)}
                                </span>
                              );
                            })}
                          </div>
                        </div>

                        {/* Quick actions */}
                        <div className="flex items-center gap-1 shrink-0" onClick={e => e.stopPropagation()}>
                          {missingCount > 0 && (
                            <span className="hidden sm:flex items-center gap-0.5 text-[9px] px-1.5 py-0.5 rounded-full border border-rose-500/20 bg-rose-500/5 text-rose-400 font-bold">
                              <AlertTriangle className="w-2.5 h-2.5" />{missingCount} missing
                            </span>
                          )}
                          <button onClick={e => { e.stopPropagation(); setPrintSample(s); }}
                            className="p-1.5 rounded-lg text-slate-500 hover:text-amber-400 hover:bg-amber-500/10 transition-colors" title="Print / PDF">
                            <Printer className="w-3.5 h-3.5" />
                          </button>
                          <button onClick={e => { e.stopPropagation(); setEditSample(s); setShowAddDialog(true); }}
                            className="p-1.5 rounded-lg text-slate-500 hover:text-cyan-400 hover:bg-cyan-500/10 transition-colors" title="Edit">
                            <Edit3 className="w-3.5 h-3.5" />
                          </button>
                          <button onClick={e => { e.stopPropagation(); requestDeleteSample(s); }}
                            className="p-1.5 rounded-lg text-slate-500 hover:text-rose-400 hover:bg-rose-500/10 transition-colors" title="Delete">
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>

                        {/* Expand chevron */}
                        <ChevronDown className={`w-4 h-4 text-slate-500 shrink-0 transition-transform duration-200 ${isExpanded ? 'rotate-180 text-cyan-400' : ''}`} />
                      </div>

                      {/* ── Expanded content ── */}
                      <AnimatePresence>
                        {isExpanded && (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: 'auto', opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.2 }}
                            className="overflow-hidden"
                          >
                            <div className="border-t border-slate-800/50 px-4 pt-3 pb-4 space-y-3">
                              {/* Identity grid */}
                              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                                <div className="bg-slate-800/40 border border-slate-800/40 rounded-lg p-2">
                                  <p className="text-[9px] text-slate-500 uppercase tracking-wider font-semibold mb-0.5">Reference ID</p>
                                  <p className="text-[10px] font-mono font-bold text-rb-brand break-all">{s.reportNumber}</p>
                                </div>
                                <div className="bg-slate-800/40 border border-slate-800/40 rounded-lg p-2">
                                  <p className="text-[9px] text-slate-500 uppercase tracking-wider font-semibold mb-0.5">Standard</p>
                                  <p className="text-[10px] font-bold text-white">{stdInfo.flag} {s.standard.replace('_', ' ')}</p>
                                </div>
                                <div className="bg-slate-800/40 border border-slate-800/40 rounded-lg p-2">
                                  <p className="text-[9px] text-slate-500 uppercase tracking-wider font-semibold mb-0.5">Date &amp; Time</p>
                                  <p className="text-[10px] font-medium text-white">
                                    {new Date(s.createdAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                                    {' '}<span className="text-slate-400">{new Date(s.createdAt).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}</span>
                                  </p>
                                </div>
                                <div className="bg-slate-800/40 border border-slate-800/40 rounded-lg p-2">
                                  <p className="text-[9px] text-slate-500 uppercase tracking-wider font-semibold mb-0.5">Site</p>
                                  <p className="text-[10px] font-medium text-white">{siteName || getCustomerName(s.customerId)}</p>
                                </div>
                              </div>

                              {/* All parameters */}
                              <div>
                                <p className="text-[9px] text-slate-500 uppercase tracking-wider font-semibold mb-1.5">
                                  All Parameters ({allParamKeys.length})
                                </p>
                                <div className="flex flex-wrap gap-1.5">
                                  {allParamKeys.map(k => {
                                    const val = params[k];
                                    const color = getParamColor(k, val);
                                    const isReq = reqParams.includes(k);
                                    return (
                                      <div key={k} className={`flex items-center gap-1 px-2 py-1 rounded-lg border ${
                                        isReq ? 'bg-cyan-500/5 border-cyan-500/20' : 'bg-rb-surface border-slate-800'
                                      }`}>
                                        <span className="text-[10px] text-slate-400 uppercase">{PARAM_LABELS[k] || k}</span>
                                        <span className="text-[11px] font-bold tabular-nums ml-0.5" style={{ color }}>{typeof val === 'number' ? val.toFixed(2) : val}</span>
                                        {PARAM_UNITS[k] && <span className="text-[9px] text-slate-600">{PARAM_UNITS[k]}</span>}
                                        {isReq && <span className="text-[8px] text-cyan-500 ml-0.5">REQ</span>}
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>

                              {/* Compliance summary bar */}
                              {missingCount > 0 && (
                                <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-rose-500/5 border border-rose-500/15">
                                  <AlertTriangle className="w-3.5 h-3.5 text-rose-400 shrink-0" />
                                  <span className="text-[10px] text-rose-400 font-medium">
                                    {missingCount} required {s.standard} parameter{missingCount !== 1 ? 's' : ''} not recorded —
                                    {' '}<span className="font-bold">{reqParams.filter(k => params[k] === undefined).join(', ')}</span>
                                  </span>
                                </div>
                              )}
                              {missingCount === 0 && (
                                <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-emerald-500/5 border border-emerald-500/15">
                                  <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400 shrink-0" />
                                  <span className="text-[10px] text-emerald-400 font-medium">All {reqParams.length} required {s.standard} parameters recorded</span>
                                </div>
                              )}

                              {s.linkedSampleId && (
                                <div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-amber-500/5 border border-amber-500/15">
                                  <FlaskConical className="w-3 h-3 text-amber-400" />
                                  <span className="text-[10px] text-amber-400">Linked Effluent Sample #{s.linkedSampleId}</span>
                                </div>
                              )}
                              {s.notes && (
                                <p className="text-[10px] text-slate-500 italic px-1">{s.notes}</p>
                              )}
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </motion.div>
                  );
                })}
              </AnimatePresence>
            </motion.div>
          )}

          {/* ══ VIEW 2: CARD GRID ══ */}
          {viewMode === 'cards' && (
            <motion.div key="cards" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
              {filtered.map((s, i) => {
                const stdInfo = getStandard(s.standard as any);
                const params = s.params || {};
                const allKeys = CORE_PARAMS.filter(k => params[k] !== undefined);
                return (
                  <motion.div
                    key={s.id}
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.04 }}
                    className="p-4 hover:border-cyan-500/30 transition-all cursor-pointer group rb-inner-card"
                    onClick={() => { setEditSample(s); setShowAddDialog(true); }}
                  >
                    {/* Card top strip */}
                    <div className="h-1 bg-gradient-to-r from-cyan-500 to-blue-500 rounded-full mb-3" />
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <div className="w-7 h-7 rounded-lg bg-cyan-500/10 border border-cyan-500/20 flex items-center justify-center">
                          <Droplets className="w-3.5 h-3.5 text-cyan-400" />
                        </div>
                        <div>
                          <p className="text-xs font-bold text-slate-200">{s.sampleName}</p>
                          <p className="text-[10px] font-mono text-slate-500">{s.reportNumber}</p>
                        </div>
                      </div>
                      <span className="text-sm">{stdInfo.flag}</span>
                    </div>

                    <div className="text-[10px] text-slate-500 mb-2">{getCustomerName(s.customerId)} · {new Date(s.createdAt).toLocaleDateString('en-GB')}</div>

                    {/* All key params as chips with color coding */}
                    <div className="flex flex-wrap gap-1 mt-2">
                      {allKeys.map(k => {
                        const val = params[k];
                        const color = getParamColor(k, val);
                        return (
                          <div key={k} className="flex items-center gap-0.5 px-1.5 py-0.5 rounded-md bg-slate-800/60 border border-slate-800/50">
                            <span className="text-[10px] text-slate-500">{PARAM_LABELS[k] || k}</span>
                            <span className="text-[10px] font-bold tabular-nums ml-0.5" style={{ color }}>{val.toFixed(1)}</span>
                            {PARAM_UNITS[k] && <span className="text-[10px] text-slate-600 ml-0.5">{PARAM_UNITS[k]}</span>}
                          </div>
                        );
                      })}
                      {Object.keys(params).filter(k => !CORE_PARAMS.includes(k)).length > 0 && (
                        <span className="px-1.5 py-0.5 rounded-md bg-slate-800/40 text-[10px] text-slate-600">
                          +{Object.keys(params).filter(k => !CORE_PARAMS.includes(k)).length} more
                        </span>
                      )}
                    </div>

                    <div className="flex items-center justify-between mt-3 pt-2 border-t border-slate-800/60">
                      <span className="text-[10px] text-slate-600">{Object.keys(params).length} parameters</span>
                      <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button onClick={(e) => { e.stopPropagation(); setPrintSample(s); }} className="p-1 rounded text-slate-500 hover:text-amber-400" title="Print / PDF">
                          <Printer className="w-3 h-3" />
                        </button>
                        <button onClick={(e) => { e.stopPropagation(); setEditSample(s); setShowAddDialog(true); }} className="p-1 rounded text-slate-500 hover:text-cyan-400">
                          <Edit3 className="w-3 h-3" />
                        </button>
                        <button onClick={(e) => { e.stopPropagation(); requestDeleteSample(s); }} className="p-1 rounded text-slate-500 hover:text-rose-400">
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </motion.div>
          )}

          {/* ══ VIEW 3: FULL DETAIL ══ */}
          {viewMode === 'detail' && (
            <motion.div key="detail" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="space-y-4">
              {filtered.map((s, i) => {
                const stdInfo = getStandard(s.standard as any);
                const params = s.params || {};
                const allParamKeys = Object.keys(params);

                // Standard-driven required parameter monitoring completeness flags
                const stdObj = ALL_STANDARDS.find(st => st.code === s.standard) || ALL_STANDARDS[0];
                const REQ_PARAMS: Record<string, string[]> = {
                  UK_EA:    ['ph', 'cod', 'bod5', 'tss', 'nh3n', 'oilgrease', 'tds', 'conductivity', 'temperature'],
                  AU_EPA:   ['ph', 'tss', 'bod5', 'cod', 'tn', 'tp', 'nh3n', 'oilgrease', 'conductivity', 'lead', 'chromium', 'cadmium', 'mercury', 'temperature'],
                  EU_UWWTD: ['ph', 'cod', 'bod5', 'tss', 'tp', 'tn', 'nh3n'],
                  US_EPA:   ['ph', 'cod', 'bod5', 'tss', 'nh3n', 'oilgrease', 'conductivity', 'temperature'],
                };
                const PARAM_LABELS_LOCAL: Record<string, string> = {
                  ph: 'pH', cod: 'COD', bod5: 'BOD₅', tss: 'TSS', nh3n: 'NH₃-N', oilgrease: 'Oil & Grease',
                  tds: 'TDS', conductivity: 'Conductivity', temperature: 'Temperature', tp: 'Total P',
                  tn: 'Total N', lead: 'Lead (Pb)', chromium: 'Chromium (Cr)', cadmium: 'Cadmium (Cd)',
                  mercury: 'Mercury (Hg)',
                };
                const reqParams = REQ_PARAMS[s.standard] || REQ_PARAMS['UK_EA'];
                const bod = params.bod5 ?? params.bod;
                const nh3 = params.nh3n ?? params.nh3;
                const og = params.oilgrease ?? params.oandg ?? params.og;
                // Build monitoring completeness flags
                const flags: { label: string; pass: boolean; detail: string }[] = reqParams.map(key => {
                  const measured = params[key] !== undefined || (key === 'bod5' && bod !== undefined) || (key === 'nh3n' && nh3 !== undefined) || (key === 'oilgrease' && og !== undefined);
                  const val = params[key] ?? (key === 'bod5' ? bod : key === 'nh3n' ? nh3 : key === 'oilgrease' ? og : undefined);
                  const label = PARAM_LABELS_LOCAL[key] || key.toUpperCase();
                  return {
                    label: `${label} measured`,
                    pass: measured,
                    detail: measured && val !== undefined ? `${Number(val).toFixed(key === 'ph' ? 1 : key.includes('lead') || key.includes('chrom') || key.includes('cad') || key.includes('merc') ? 3 : 0)} ${key === 'ph' ? '' : key === 'conductivity' ? 'µS/cm' : key === 'temperature' ? '°C' : 'mg/L'}`.trim() : 'Not recorded',
                  };
                });
                const failCount = flags.filter(f => !f.pass).length;

                return (
                  <motion.div
                    key={s.id}
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.04 }}
                    className="overflow-hidden rb-inner-card"
                  >
                    {/* Header bar */}
                    <div className="flex items-center justify-between px-4 py-3 bg-rb-surface border-b border-slate-800/60">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-lg bg-cyan-500/10 border border-cyan-500/20 flex items-center justify-center">
                          <Droplets className="w-4 h-4 text-cyan-400" />
                        </div>
                        <div>
                          <p className="text-sm font-bold text-slate-200">{s.sampleName}</p>
                          <div className="flex items-center gap-2 mt-0.5">
                            <span className="text-[10px] font-mono text-slate-500">{s.reportNumber}</span>
                            <span className="text-[10px] text-slate-600">·</span>
                            <span className="text-[10px] text-slate-500">{getCustomerName(s.customerId)}</span>
                            <span className="text-[10px] text-slate-600">·</span>
                            <span className="text-[10px] text-slate-500">{new Date(s.createdAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}</span>
                            <span className="text-sm">{stdInfo.flag}</span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold border ${
                          failCount === 0 ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' : 'bg-rose-500/10 text-rose-400 border-rose-500/20'
                        }`}>
                          {failCount === 0 ? '✓ All flags pass' : `${failCount} flag${failCount > 1 ? 's' : ''}`}
                        </span>
                        <button onClick={() => setPrintSample(s)} className="p-1.5 rounded-lg text-slate-500 hover:text-amber-400 hover:bg-amber-500/10 transition-colors" title="Print / PDF">
                          <Printer className="w-3.5 h-3.5" />
                        </button>
                        <button onClick={() => { setEditSample(s); setShowAddDialog(true); }} className="p-1.5 rounded-lg text-slate-500 hover:text-cyan-400 hover:bg-cyan-500/10 transition-colors">
                          <Edit3 className="w-3.5 h-3.5" />
                        </button>
                        <button onClick={() => requestDeleteSample(s)} className="p-1.5 rounded-lg text-slate-500 hover:text-rose-400 hover:bg-rose-500/10 transition-colors">
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>

                    <div className="p-4 grid grid-cols-1 lg:grid-cols-2 gap-4">
                      {/* All parameters */}
                      <div>
                        <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2">All Parameters ({allParamKeys.length})</p>
                        <div className="flex flex-wrap gap-1.5">
                          {allParamKeys.map(k => {
                            const val = params[k];
                            const color = getParamColor(k, val);
                            return (
                              <div key={k} className="flex items-center gap-1 px-2 py-1.5 rb-card-sm">
                                <span className="text-[10px] text-slate-500 uppercase">{PARAM_LABELS[k] || k}</span>
                                <span className="text-[11px] font-bold tabular-nums ml-0.5" style={{ color }}>{typeof val === 'number' ? val.toFixed(2) : val}</span>
                                {PARAM_UNITS[k] && <span className="text-[10px] text-slate-600">{PARAM_UNITS[k]}</span>}
                              </div>
                            );
                          })}
                        </div>
                        {s.notes && (
                          <p className="text-[10px] text-slate-500 mt-2 italic">{s.notes}</p>
                        )}
                      </div>

                      {/* Regulatory compliance flags */}
                      <div>
                        <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2">Regulatory Compliance Flags</p>
                        <div className="space-y-1">
                          {flags.map((f, fi) => (
                            <div key={fi} className={`flex items-center gap-2 px-2.5 py-1.5 rounded-lg border ${
                              f.pass ? 'bg-emerald-500/5 border-emerald-500/15' : 'bg-rose-500/5 border-rose-500/20'
                            }`}>
                              {f.pass
                                ? <CheckCircle2 className="w-3 h-3 text-emerald-400 shrink-0" />
                                : <AlertTriangle className="w-3 h-3 text-rose-400 shrink-0" />
                              }
                              <span className={`text-[10px] font-medium flex-1 ${f.pass ? 'text-emerald-400' : 'text-rose-400'}`}>{f.label}</span>
                              <span className={`text-[10px] font-mono ${f.pass ? 'text-slate-500' : 'text-rose-400'}`}>{f.detail}</span>
                            </div>
                          ))}
                        </div>
                        {s.linkedSampleId && (
                          <div className="flex items-center gap-1.5 mt-2 px-2.5 py-1.5 rounded-lg bg-amber-500/5 border border-amber-500/15">
                            <FlaskConical className="w-3 h-3 text-amber-400" />
                            <span className="text-[10px] text-amber-400">Linked Effluent Sample #{s.linkedSampleId}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </motion.div>
          )}

        </AnimatePresence>
      )}

      {/* ─── ADD / EDIT DIALOG ─── */}
      <AddSampleDialog
        open={showAddDialog}
        customers={customers.map(c => ({ id: c.id, name: c.name, company: c.company }))}
        onClose={() => { setShowAddDialog(false); setEditSample(null); }}
        onSave={handleAddSave}
      />

      {/* ─── DELETE CONFIRM ─── */}
      <ConfirmDialog
        open={deleteId !== null}
        title="Delete Sample"
        message="Are you sure you want to delete this influent sample? This action cannot be undone."
        onConfirm={handleDelete}
        onCancel={() => setDeleteId(null)}
      />

      {/* ─── PRINT MODAL ─── */}
      {printSample && (
        <SamplePrintModal
          sample={printSample}
          customers={customers}
          sites={sites}
          onClose={() => setPrintSample(null)}
        />
      )}
    </div>
  );
}
